/**
 * Встраивание внешнего содержимого: видео и живые демо дашбордов.
 *
 * Редактор вставляет обычную ссылку (страницу YouTube, RuTube, VK, Vimeo
 * или адрес дашборда), компонент сам приводит её к адресу embed-версии.
 * Если сервис неизвестен — ссылка используется как есть, так что подойдёт
 * любой iframe-источник.
 */
export interface EmbedContent {
  /** Включён ли блок на странице */
  enabled?: boolean;
  /** iframe — дашборд или произвольный сайт; video — видеоплеер */
  kind?: "iframe" | "video";
  /** Ссылка: страница видео или адрес embed */
  url: string;
  /** Заголовок для доступности и подписи */
  title?: string;
  /** Пропорции кадра: 16/9, 4/3, 1/1, 21/9 */
  aspect?: string;
  /** Пояснение под блоком */
  note?: string;
}

const ASPECT: Record<string, string> = {
  "16/9": "aspect-[16/9]",
  "4/3": "aspect-[4/3]",
  "3/2": "aspect-[3/2]",
  "1/1": "aspect-square",
  "21/9": "aspect-[21/9]",
  "9/16": "aspect-[9/16]",
  "4/5": "aspect-[4/5]",
};

/**
 * Приводим ссылку к embed-виду. Возвращает исходную строку, если сервис
 * не распознан — тогда она используется напрямую как src для iframe.
 */
export function toEmbedUrl(rawUrl: string, kind: "iframe" | "video" = "iframe"): string {
  const url = (rawUrl ?? "").trim();
  if (!url) return "";

  // уже embed-адрес — не трогаем
  if (/\/(embed|play\/embed|video_ext\.php|embed\/videos)/i.test(url)) return url;

  // YouTube: watch?v=ID, youtu.be/ID, shorts/ID
  const yt =
    url.match(/(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{6,})/i) ||
    url.match(/youtu\.be\/([\w-]{6,})/i) ||
    url.match(/youtube\.com\/shorts\/([\w-]{6,})/i);
  if (yt) {
    const id = yt[1];
    const params = new URLSearchParams({ rel: "0", modestbranding: "1" });
    return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
  }

  // RuTube: rutube.ru/video/ID/ (иногда с хешем)
  const rt = url.match(/rutube\.ru\/video\/([a-z0-9]+)/i);
  if (rt) return `https://rutube.ru/play/embed/${rt[1]}`;

  // VK Видео: vk.com/video-123_456 или vkvideo.ru/video-123_456
  const vk = url.match(/vk(?:video)?\.(?:com|ru)\/video(-?\d+)_(\d+)/i);
  if (vk) return `https://vk.com/video_ext.php?oid=${vk[1]}&id=${vk[2]}&hd=2`;

  // Vimeo: vimeo.com/ID
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  // Dailymotion
  const dm = url.match(/dailymotion\.com\/video\/([a-z0-9]+)/i);
  if (dm) return `https://www.dailymotion.com/embed/video/${dm[1]}`;

  // Kinescope, VK Play и прочие — оставляем как есть
  return url;
}

/** Это видео-сервис или произвольный iframe? */
export function detectKind(url: string): "iframe" | "video" {
  return /youtube|youtu\.be|rutube|vk\.com\/video|vkvideo|vimeo|dailymotion|kinescope/i.test(
    url ?? ""
  )
    ? "video"
    : "iframe";
}

export default function EmbedFrame({
  embed,
  className = "",
  lazy = true,
}: {
  embed: EmbedContent;
  className?: string;
  lazy?: boolean;
}) {
  if (!embed || embed.enabled === false || !embed.url) return null;

  const kind = embed.kind ?? detectKind(embed.url);
  const src = toEmbedUrl(embed.url, kind);
  const aspectClass = ASPECT[embed.aspect ?? "16/9"] ?? ASPECT["16/9"];

  return (
    <div className={className}>
      <div
        className={`relative w-full ${aspectClass} overflow-hidden rounded-2xl border border-stone-200 dark:border-white/[0.09] bg-stone-100 dark:bg-white/[0.04]`}
      >
        <iframe
          src={src}
          title={embed.title ?? "Встроенный блок"}
          loading={lazy ? "lazy" : "eager"}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 w-full h-full border-0"
        />
      </div>
      {embed.note && (
        <p className="mt-2.5 text-xs text-stone-500 dark:text-white/50">{embed.note}</p>
      )}
    </div>
  );
}
